import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";

import { EmailPreviewType } from "../../../../types/onboarding";
import useEmailPreview from "../../../../hooks/useEmailPreview";
import EmailPreview, { previewErrorMessage } from "./EmailPreview";

interface EmailPreviewDialogProps {
  visible: boolean;
  prospectId: string;
  type: EmailPreviewType;
  // GO_LIVE only: the stations the email should name.
  stationIds?: string[];
  header: string;
  // What pressing send does beyond the email, in the words the old confirmation
  // used — the preview shows the email, not the rest of the action.
  intro?: React.ReactNode;
  sendLabel: string;
  sendIcon?: string;
  // The panel's existing action, unchanged: it reloads and toasts as before.
  // Resolves once finished; the dialog closes either way, since failures are
  // reported on the panel behind it.
  onSend: () => Promise<unknown>;
  onHide: () => void;
  // The send action's own error mapper, used for refusals: the preview refuses
  // with the same statuses as the send, so the same words apply.
  errorMessage: (err: any) => string;
  // Off when the email is a by-product of the action: publishing goes ahead even
  // when its confirmation email fails, so a broken email must not stop it.
  blockOnErrors?: boolean;
}

/**
 * The confirmation step of every onboarding action that emails the customer
 * (CWA-173): the agent sees the email itself — recipient, subject, body and
 * attachments — before sending it. Nothing is sent or recorded until Send.
 */
const EmailPreviewDialog: React.FC<EmailPreviewDialogProps> = ({
  visible,
  prospectId,
  type,
  stationIds,
  header,
  intro,
  sendLabel,
  sendIcon = "pi pi-envelope",
  onSend,
  onHide,
  errorMessage,
  blockOnErrors = true,
}) => {
  const { preview, loading, error, retry } = useEmailPreview(
    prospectId,
    type,
    stationIds,
    visible
  );
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (visible) setSubmitting(false);
  }, [visible]);

  // Without blocking, the preview is information only: publishing must not come
  // to depend on the preview endpoint answering.
  const blocked =
    blockOnErrors && (loading || !!error || !preview || !preview.can_send);

  const send = async () => {
    setSubmitting(true);
    try {
      await onSend();
      onHide();
    } finally {
      setSubmitting(false);
    }
  };

  const footer = (
    <div>
      <Button label="Cancel" text onClick={onHide} disabled={submitting} />
      <Button
        label={sendLabel}
        icon={sendIcon}
        onClick={send}
        loading={submitting}
        disabled={submitting || blocked}
      />
    </div>
  );

  return (
    <Dialog
      header={header}
      visible={visible}
      onHide={onHide}
      footer={footer}
      style={{ width: "52rem", maxWidth: "95vw" }}
    >
      {intro && <p className="mt-0">{intro}</p>}
      <EmailPreview
        preview={preview}
        loading={loading}
        errorMessage={error ? previewErrorMessage(error, errorMessage) : null}
        onRetry={retry}
      />
    </Dialog>
  );
};

export default EmailPreviewDialog;
